import type { ActionDescriptor, ActionSource } from '@krp/client-runtime'

/**
 * Реестр действий оболочки и общих элементов (UI-ACTIONS): шапка, меню,
 * вкладки, вход, общие кнопки состояний. Оболочка видов ресурса не знает —
 * в этих записях их нет (ADR-18, проверяет реестр). Записи окон пакетов —
 * в самих пакетах (`packages/<пакет>/src/actions/registerActions.ts`).
 *
 * Тесты — путь от корня репозитория и ТОЧНОЕ имя: тест реестра
 * (`tests/actionRegistry.test.ts`) находит каждый, иначе CI красный.
 */
const APP = 'frontend/apps/web/src/tests/app.test.tsx'
const ACTIONS = 'frontend/apps/web/src/tests/actionInfo.test.tsx'
const SECTIONS = 'frontend/apps/web/src/tests/sections.test.tsx'
const RECEIPT_SOURCES = 'frontend/apps/web/src/tests/receiptsSources.test.tsx'
const UPDATE = 'frontend/apps/web/src/tests/update.test.tsx'
const CONNECTION = 'frontend/apps/web/src/tests/connection.test.tsx'
const GATE = 'frontend/apps/web/src/tests/sessionGate.test.tsx'
const THEME = 'frontend/apps/web/src/tests/theme.test.tsx'
const HEALTH = 'frontend/apps/web/src/tests/health.test.tsx'
const WINDOWS = 'frontend/packages/client-runtime/src/windows/tests/windows.test.ts'
const E2E = 'tests/ResourcePlatform/E2E/specs'

const SHELL_ACTIONS: readonly ActionDescriptor[] = [
  {
    id: 'shell.sign-in',
    windows: ['shell'],
    label: 'Войти',
    summary:
      'Открывает вход по учётной записи Kalmykov. После входа возвращает на тот же экран, где вы были, — несохранённая работа на нём не пропадает.',
    effect: 'view',
    effectText: 'Сама ничего не сохраняет: вход проверяет сервер входа.',
    access: 'Все сотрудники с учётной записью Kalmykov.',
    disabledWhen: 'Кнопки нет, пока вы вошли.',
    evidence: {
      unit: [{ file: APP, name: 'Missing_session_offers_sign_in_via_gateway_with_return_address' }],
      stand: [
        { file: `${E2E}/access.spec.ts`, name: 'Account_of_another_application_gets_no_access_page_not_a_login_loop' },
        { file: `${E2E}/session.spec.ts`, name: 'Logout_then_another_user_sees_nothing_of_the_previous_session' },
      ],
    },
  },
  {
    id: 'shell.home',
    windows: ['shell'],
    label: 'Kalmykov Retail — главная',
    summary: 'Открывает главную страницу со списком разделов.',
    effect: 'view',
    evidence: { unit: [{ file: ACTIONS, name: 'Brand_and_home_tiles_open_their_windows' }] },
  },
  {
    id: 'shell.back',
    windows: ['shell'],
    label: 'Назад',
    summary:
      'Возвращает туда, откуда вы пришли в это окно. Если пришли не из окна — в последнюю вкладку, если вкладок нет — на главную.',
    effect: 'view',
    disabledWhen: 'Кнопки нет, когда возвращаться некуда.',
    evidence: {
      unit: [{ file: WINDOWS, name: 'Close_goes_to_origin_then_last_tab_then_home' }],
      stand: [{ file: `${E2E}/session.spec.ts`, name: 'Tab_is_created_only_by_the_button_and_back_returns_where_you_came_from' }],
    },
  },
  {
    id: 'shell.tab',
    windows: ['shell'],
    label: 'Вкладка',
    summary:
      'Кладёт открытое окно в полосу вкладок под шапкой — как закладку: потом в него можно вернуться одним нажатием, и окно вспомнит, на чём вы остановились. Нажатая кнопка убирает вкладку. Переходы по меню вкладок сами не создают.',
    effect: 'device',
    effectText: 'Вкладки запоминаются на этом устройстве для вас; данные на сервере не меняются.',
    disabledWhen: 'Когда вкладок уже 10 — сначала уберите ненужную.',
    evidence: {
      unit: [
        { file: APP, name: 'Menu_navigation_does_not_create_a_tab_only_the_button_does' },
        { file: WINDOWS, name: 'Tab_button_creates_one_tab_per_window_with_its_rid' },
      ],
      stand: [{ file: `${E2E}/session.spec.ts`, name: 'Tab_is_created_only_by_the_button_and_back_returns_where_you_came_from' }],
    },
  },
  {
    id: 'shell.tabs',
    windows: ['shell'],
    label: 'Полоса вкладок',
    summary:
      'Ваши вкладки. Нажатие на вкладку открывает её окно в том виде, в каком вы его оставили; крестик убирает вкладку из полосы — само окно и данные он не трогает.',
    effect: 'device',
    effectText: 'Меняется только список вкладок на этом устройстве.',
    evidence: {
      unit: [
        { file: WINDOWS, name: 'Switching_tabs_saves_work_of_the_left_window_and_restores_the_entered_one' },
        { file: APP, name: 'Filter_and_tabs_of_the_user_survive_refresh' },
        { file: ACTIONS, name: 'Tab_cross_removes_the_tab_and_leaves_the_screen_as_it_was' },
      ],
    },
  },
  {
    id: 'shell.hints',
    windows: ['shell'],
    label: 'Подсказки',
    summary:
      'Включает и выключает эти подсказки ⓘ у кнопок. Выключите, когда интерфейс знаком, — кнопки останутся те же.',
    effect: 'device',
    effectText: 'Выбор запоминается на этом устройстве для всех, кто за ним работает.',
    evidence: {
      unit: [
        { file: ACTIONS, name: 'Hints_are_on_by_default_and_the_choice_survives_refresh_on_this_device' },
        { file: ACTIONS, name: 'Hints_off_leave_the_buttons_without_info' },
      ],
    },
  },
  {
    id: 'shell.theme',
    windows: ['shell'],
    label: 'Тема оформления',
    summary:
      'Свитч под знаком «K»: светлая тема или тёмная. При первом заходе тема выбирается по времени суток (день — светлая, ночь — тёмная); на данные не влияет.',
    effect: 'device',
    effectText: 'Выбор запоминается на этом устройстве и переживает перезагрузку и выход из приложения.',
    evidence: {
      unit: [
        { file: THEME, name: 'First_visit_picks_the_theme_by_time_of_day' },
        { file: THEME, name: 'Theme_choice_survives_reload_on_this_device_and_sign_out' },
      ],
    },
  },
  {
    id: 'shell.health',
    windows: ['shell'],
    label: 'Связь',
    summary:
      'Показывает, отвечают ли сервер программы и то, с чем работает открытый экран. Зелёная точка — всё отвечает; жёлтая и красная пульсируют, когда что-то отстаёт или не отвечает. Нажатие открывает окошко: что именно не так, когда был последний ответ, что сделать, и кнопку «Скопировать» для администратора.',
    effect: 'view',
    effectText: 'Сама ничего не меняет; «Повторить» в окошке только проверяет сервер сейчас.',
    evidence: {
      unit: [
        { file: HEALTH, name: 'Dot_in_the_header_is_green_while_the_server_answers' },
        { file: HEALTH, name: 'Lost_server_turns_the_dot_red_and_the_mini_window_names_the_server' },
      ],
    },
  },
  {
    id: 'shell.sign-out',
    windows: ['shell'],
    label: 'Выйти',
    summary:
      'Завершает вашу работу на этом устройстве во всех вкладках браузера: забывает ваши вкладки и черновики и закрывает живую связь с сервером. Следующий человек за этим устройством ничего вашего не увидит.',
    effect: 'device',
    effectText: 'Стирает с устройства ваши вкладки и черновики; сохранённое на сервере не трогает.',
    evidence: {
      unit: [{ file: APP, name: 'Sign_out_clears_user_scope_and_leaves_via_gateway_logout' }],
      stand: [
        { file: `${E2E}/live-channel.spec.ts`, name: 'Sign_out_closes_the_channel' },
        { file: `${E2E}/session.spec.ts`, name: 'Logout_then_another_user_sees_nothing_of_the_previous_session' },
      ],
    },
  },
  {
    id: 'shell.menu',
    windows: ['shell'],
    label: 'Раздел меню',
    summary:
      'Открывает раздел. Вкладку не создаёт: чтобы потом вернуться сюда одним нажатием, нажмите «Вкладка» в шапке.',
    effect: 'view',
    access: 'Пункты видны всем; внутри раздела видно только то, на что у вас есть права.',
    evidence: {
      unit: [
        { file: APP, name: 'Menu_navigation_does_not_create_a_tab_only_the_button_does' },
        { file: SECTIONS, name: 'Menu_lists_business_sections_and_never_names_a_resource_kind' },
      ],
      stand: [{ file: `${E2E}/placement.spec.ts`, name: 'Both_onec_bases_open_in_their_own_sections' }],
    },
  },
  {
    id: 'shell.home-link',
    windows: ['shell'],
    label: 'Раздел на главной',
    summary: 'Открывает раздел — то же, что пункт меню слева.',
    effect: 'view',
    evidence: { unit: [{ file: ACTIONS, name: 'Brand_and_home_tiles_open_their_windows' }] },
  },
  {
    id: 'shell.not-found-home',
    windows: ['shell'],
    label: 'На главную',
    summary: 'Адрес не ведёт ни в одно окно — кнопка уводит на главную страницу со списком разделов.',
    effect: 'view',
    evidence: { unit: [{ file: APP, name: 'Unknown_address_shows_not_found_inside_the_shell' }] },
  },
  {
    id: 'shell.modal-close',
    windows: ['shell'],
    label: 'Закрыть окно',
    summary:
      'Закрывает окно, открытое поверх экрана, и возвращает к экрану под ним. То же делает клавиша Esc. Несохранённое в окне не сохраняется.',
    effect: 'view',
    evidence: { unit: [{ file: ACTIONS, name: 'Window_over_the_screen_closes_by_cross_and_by_escape_and_escape_in_a_hint_closes_only_the_hint' }] },
  },
  {
    id: 'shell.section-tab',
    windows: ['shell'],
    label: 'Вкладка раздела',
    summary:
      'Переключает источник внутри раздела: общий каталог, базу или кабинет. У каждой вкладки своё окно и свой адрес; «Назад» уводит из раздела, а не по вкладкам.',
    effect: 'view',
    access: 'Видны источники, к которым у вас есть право этого раздела.',
    evidence: {
      unit: [
        { file: SECTIONS, name: 'Common_catalog_is_the_first_tab_and_each_source_is_a_tab_that_lives_in_the_address' },
      ],
    },
  },
  {
    id: 'shell.section-source-change',
    windows: ['shell'],
    label: 'Сменить источник',
    summary:
      'Возвращает к плиткам источников раздела, чтобы выбрать другую базу или кабинет. Открытое окно закрывается без сохранения: ничего не отправляется и не меняется.',
    effect: 'view',
    access: 'Видна в разделах, которые начинаются с выбора источника.',
    evidence: {
      unit: [{ file: RECEIPT_SOURCES, name: 'Opened_source_is_named_in_a_bar_with_only_its_own_tabs_and_change_goes_back_to_the_tiles' }],
    },
  },
  {
    id: 'common.retry',
    windows: ['common'],
    label: 'Повторить',
    summary: 'Данные не пришли: служба недоступна или ответила ошибкой. Кнопка запрашивает их ещё раз.',
    effect: 'read',
    effectText: 'Только перечитывает — ничего не отправляет и не меняет.',
    disabledWhen: 'Видна только при ошибке загрузки.',
    evidence: {
      unit: [
        {
          file: 'frontend/packages/platform-ui/src/features/resources/tests/ResourcesScreen.test.tsx',
          name: 'Forbidden_and_unavailable_are_distinct_states_with_server_text',
        },
      ],
      stand: [{ file: `${E2E}/resilience.spec.ts`, name: 'Network_loss_is_named_and_recovers_with_a_retry' }],
    },
  },
  {
    id: 'shell.update',
    windows: ['shell'],
    label: 'Обновить',
    summary:
      'Вышла новая версия приложения: кнопка перезагружает страницу и берёт свежую сборку. Выключена, пока открыт чек или идёт оплата, — обновитесь после закрытия чека; сама страница никогда не перезагружается.',
    effect: 'device',
    effectText: 'Перезагружает приложение на этом устройстве; данные на сервере не меняются, вход сохраняется.',
    disabledWhen: 'Пока открыт чек или идёт оплата.',
    evidence: {
      unit: [
        { file: UPDATE, name: 'Update_button_reloads_the_page_for_the_new_build' },
        {
          file: 'frontend/apps/web/src/tests/routeError.test.tsx',
          name: 'Stale_chunk_is_named_as_new_version_and_update_button_reloads',
        },
        { file: UPDATE, name: 'Open_receipt_blocks_the_reload_and_says_to_update_after_closing_it' },
      ],
    },
  },
  {
    id: 'shell.update-later',
    windows: ['shell'],
    label: 'Позже',
    summary: 'Прячет плашку об обновлении на полчаса, потом она появится снова: закрыть её насовсем нельзя.',
    effect: 'device',
    effectText: 'Только прячет плашку на этом устройстве.',
    evidence: { unit: [{ file: UPDATE, name: 'Later_hides_the_banner_for_a_while_and_it_comes_back' }] },
  },
  {
    id: 'shell.connection-retry',
    windows: ['shell'],
    label: 'Повторить сейчас',
    summary:
      'Связь с сервером пропала: окно по центру проверяет сервер само с растущей паузой. Кнопка проверяет немедленно, не дожидаясь паузы; ответил — окно закрывается и данные на экране перечитываются.',
    effect: 'read',
    effectText: 'Только спрашивает сервер «кто я»; команды и продажи не повторяет.',
    disabledWhen: 'Кнопка есть только в окне связи, пока сервер не отвечает.',
    evidence: { unit: [{ file: CONNECTION, name: 'Retry_now_checks_the_server_immediately_and_the_window_closes_on_answer' }] },
  },
  {
    id: 'shell.gate-retry',
    windows: ['shell'],
    label: 'Повторить',
    summary:
      'Сервер входа не ответил при открытии программы. Программа проверяет его сама с растущей паузой; кнопка проверяет немедленно. Ответил — вход открывается без обновления страницы.',
    effect: 'read',
    effectText: 'Только спрашивает сервер «кто я»; ничего не сохраняет и не повторяет команд.',
    disabledWhen: 'Кнопка есть только на экране «Сервер входа недоступен».',
    evidence: { unit: [{ file: GATE, name: 'Retry_button_asks_the_login_server_again_at_once_and_the_gate_opens_when_it_answers' }] },
  },
  {
    id: 'common.reread-after-conflict',
    windows: ['common'],
    label: 'Перечитать',
    summary:
      'Команду не выполнили: данные успели измениться у другого человека или исход неизвестен. Кнопка перечитывает их с сервера, чтобы вы решили заново на свежих данных, — сама команда не повторяется.',
    effect: 'read',
    effectText: 'Только перечитывает.',
    disabledWhen: 'Видна после отказа из-за изменившихся данных или неизвестного исхода.',
    evidence: {
      unit: [
        {
          file: 'frontend/packages/platform-ui/src/shared/tests/errorViews.test.tsx',
          name: 'Conflict_error_offers_a_reread_button_that_calls_on_reload',
        },
      ],
    },
  },
]

/** Источник реестра для composition: оболочка объявляет `shell.*` и общие `common.*`. */
export const SHELL_ACTION_SOURCE: ActionSource = { id: 'shell', actions: SHELL_ACTIONS, extraPrefixes: ['common'] }
